/*
 * Email sign-up pop-up (snippets/newsletter-popup.liquid): the four steps of Klaviyo's own form.
 *
 * 1. The offer ("Up to $300 Off Your First Order") and one button.
 * 2. Email: sent to the Klaviyo email list (client subscriptions API: the public key, no
 *    secret), so Klaviyo's welcome email with the offer goes out as before.
 * 3. Phone (when an SMS list is set): the same profile, subscribed to texts on the SMS list,
 *    with the consent wording Klaviyo's form showed. "No, Thanks" skips it.
 * 4. Thank you.
 *
 * The offer ([amount] in the words) is shown in the shopper's currency: the store's amount at
 * Shopify's rate, rounded down to a round figure so it never promises more (CA$300 is $225 in
 * the U.S. at 0.75). Without the key and list nothing is sent, and the pop-up only opens in
 * theme previews. It waits a few seconds (on phones, also a third of the page), stays away from
 * the bag, search, drawers and the cookie banner, opens once per visit and not again for the
 * chosen days once closed; never after a sign-up, and not for a shopper Klaviyo already knows
 * (arrived from its emails). Closed without signing up, a small tab can reopen it; it keeps
 * clear of bars at the bottom of the screen (assets/ldj-embeds.js). ?ldj-popup=show opens it.
 */
(() => {
  'use strict';

  const script = document.querySelector('script[data-ldj-popup-config]');
  const template = document.getElementById('LdjPopupTemplate');
  if (!script || !template || window.LdjPopup) return;
  window.LdjPopup = true;

  const config = script.dataset;
  const root = document.documentElement;
  const ready = config.ready === 'true' && config.key && config.list;
  const role = window.Shopify?.theme?.role;
  const preview = Boolean(window.Shopify?.designMode) || (role && role !== 'main');
  const params = new URLSearchParams(window.location.search);
  const forced = params.get('ldj-popup') === 'show';
  if (!ready && !preview && !forced) return;

  const DAY = 86400000;
  const KEYS = { joined: 'ldj:popup:joined', closed: 'ldj:popup:closed', seen: 'ldj:popup:seen', tab: 'ldj:popup:tab-hidden' };
  const read = (key, area = 'localStorage') => {
    try { return window[area].getItem(key); } catch (_error) { return null; }
  };
  const write = (key, value, area = 'localStorage') => {
    try { window[area].setItem(key, value); } catch (_error) { /* private mode */ }
  };
  const days = Math.max(1, Number(config.days) || 14);
  const within = (key) => {
    const at = Number(read(key)) || 0;
    return at && Date.now() - at < days * DAY;
  };

  // Someone Klaviyo already knows: they came from one of its emails (its _kx link, or its
  // cookie holding their exchange ID or email). Klaviyo's form wasn't shown to them either.
  const knownToKlaviyo = () => {
    if (params.has('_kx')) return true;
    const cookie = document.cookie.split('; ').find((entry) => entry.startsWith('__kla_id='));
    if (!cookie) return false;
    try {
      const data = JSON.parse(atob(decodeURIComponent(cookie.slice('__kla_id='.length))));
      return Boolean(data.$exchange_id || data.$email || data.$phone_number);
    } catch (_error) {
      return false;
    }
  };

  const joined = () => Boolean(read(KEYS.joined));

  // ---------------------------------------------------------------- the offer, in the shopper's currency

  const offerText = () => {
    const base = Number(config.offer) || 300;
    const currency = window.Shopify?.currency?.active || 'CAD';
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    let value = base * rate;
    const step = value < 1000 ? 5 : value < 10000 ? 50 : 500;
    value = Math.max(step, Math.floor(value / step) * step);
    const locale = currency === 'CAD' ? 'en-CA' : currency === 'USD' ? 'en-US' : (root.lang || 'en');
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    } catch (_error) {
      return `$${value}`;
    }
  };

  const fillOffer = (scope) => {
    const amount = offerText();
    scope.querySelectorAll('[data-ldj-offer]').forEach((element) => {
      if (!element.dataset.ldjOfferText) element.dataset.ldjOfferText = element.textContent;
      element.textContent = element.dataset.ldjOfferText.replace(/\[amount\]/gi, amount);
    });
  };

  // ---------------------------------------------------------------- Klaviyo

  const subscribe = async ({ email, phone, list }) => {
    const attributes = { email };
    if (phone) {
      attributes.phone_number = phone;
      attributes.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
    }
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(config.key)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2025-07-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: config.source || 'Website pop-up',
            profile: { data: { type: 'profile', attributes } }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      })
    });
    if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
  };

  // A mobile number in the form Klaviyo needs (+14035550123). U.S. and Canadian numbers may be
  // typed without the +1; others need their country code.
  const phoneNumber = (value) => {
    const text = String(value || '').trim();
    const digits = text.replace(/\D/g, '');
    if (text.startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : '';
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return '';
  };

  // ---------------------------------------------------------------- the pop-up

  let dialog = null;
  let tab = null;
  let email = '';

  const stylesheet = () => new Promise((resolve) => {
    if (document.querySelector('link[data-ldj-popup-css]')) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = config.css;
    link.dataset.ldjPopupCss = '';
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.append(link);
  });

  const steps = () => Array.from(dialog.querySelectorAll('[data-step]:not(dialog)'));

  const show = (name, { focus = true } = {}) => {
    dialog.dataset.step = name;
    steps().forEach((step) => {
      step.hidden = step.dataset.step !== name;
    });
    if (!focus) return;
    const step = dialog.querySelector(`.ldj-popup__step[data-step="${name}"]`);
    const field = step?.querySelector('input:not(.ldj-popup__trap)');
    // On a phone, the keyboard comes up only once the shopper chose to go on.
    if (field && (name !== 'email' || !window.matchMedia('(pointer: coarse)').matches || dialog.dataset.touched)) field.focus({ preventScroll: true });
    else step?.focus({ preventScroll: true });
  };

  const showTab = () => {
    if (!tab || joined() || within(KEYS.tab)) return;
    fillOffer(tab);
    tab.hidden = false;
  };

  const close = () => {
    if (!dialog?.open) return;
    if (!dialog.dataset.joined) write(KEYS.closed, String(Date.now()));
    dialog.classList.remove('is-open');
    const done = () => {
      dialog.close();
      root.classList.remove('ldj-popup-open');
      if (!dialog.dataset.joined) showTab();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else window.setTimeout(done, 220);
  };

  const wire = () => {
    const fragment = template.content.cloneNode(true);
    dialog = fragment.querySelector('[data-ldj-popup]');
    tab = fragment.querySelector('[data-ldj-popup-tab]');
    document.body.append(dialog);
    if (tab) document.body.append(tab);
    fillOffer(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-ldj-popup-close]') || event.target === dialog) {
        close();
        return;
      }
      if (event.target.closest('[data-ldj-popup-next]')) {
        dialog.dataset.touched = '';
        show('email');
        return;
      }
      if (event.target.closest('[data-ldj-popup-skip]')) show('done');
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    const fail = (form, input, message) => {
      const error = form.querySelector('[data-ldj-popup-error]');
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    };
    const clear = (form, input) => {
      form.querySelector('[data-ldj-popup-error]').hidden = true;
      input.removeAttribute('aria-invalid');
    };
    const busyButton = (form, on) => {
      const submit = form.querySelector('button[type="submit"]');
      submit.disabled = on;
      if (on) submit.setAttribute('aria-busy', 'true');
      else submit.removeAttribute('aria-busy');
    };

    const emailForm = dialog.querySelector('[data-ldj-popup-form]');
    const smsForm = dialog.querySelector('[data-ldj-popup-sms]');

    emailForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = emailForm.querySelector('input[type="email"]');
      clear(emailForm, input);
      if (emailForm.elements.company?.value) return; // a bot filled the hidden field
      const value = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
        fail(emailForm, input, 'Please enter a valid email address.');
        return;
      }
      if (!ready) {
        fail(emailForm, input, 'Preview only: add the Klaviyo public key and list ID in Theme settings to collect sign-ups.');
        return;
      }
      busyButton(emailForm, true);
      try {
        await subscribe({ email: value, list: config.list });
        email = value;
        dialog.dataset.joined = '';
        write(KEYS.joined, String(Date.now()));
        show(smsForm && config.smsList ? 'sms' : 'done');
      } catch (_error) {
        fail(emailForm, input, 'Something went wrong. Please try again in a moment.');
      } finally {
        busyButton(emailForm, false);
      }
    });

    smsForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = smsForm.querySelector('input[type="tel"]');
      clear(smsForm, input);
      const phone = phoneNumber(input.value);
      if (!phone) {
        fail(smsForm, input, 'Please enter a mobile number, with its country code outside the U.S. and Canada (e.g. +44…).');
        return;
      }
      busyButton(smsForm, true);
      try {
        await subscribe({ email, phone, list: config.smsList });
        show('done');
      } catch (_error) {
        fail(smsForm, input, 'That number didn’t go through. Please check it, or choose “No, Thanks”.');
      } finally {
        busyButton(smsForm, false);
      }
    });

    tab?.addEventListener('click', (event) => {
      if (event.target.closest('[data-ldj-popup-tab-close]')) {
        write(KEYS.tab, String(Date.now()));
        tab.hidden = true;
        return;
      }
      if (event.target.closest('[data-ldj-popup-reopen]')) {
        tab.hidden = true;
        open({ reopened: true });
      }
    });
  };

  const open = async ({ reopened = false } = {}) => {
    if (dialog?.open) return;
    await stylesheet();
    if (!dialog) wire();
    write(KEYS.seen, '1', 'sessionStorage');
    show(reopened && dialog.dataset.step === 'email' ? 'email' : dialog.dataset.joined ? 'done' : 'intro', { focus: false });
    if (tab) tab.hidden = true;
    dialog.showModal();
    root.classList.add('ldj-popup-open');
    window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    // Focus goes to the step itself (the dialog is named by its heading), never a field: on
    // phones the keyboard would cover the offer, and a ring round the button isn't needed.
    dialog.querySelector(`.ldj-popup__step[data-step="${dialog.dataset.step}"]`)?.focus({ preventScroll: true });
  };

  // ---------------------------------------------------------------- when

  // Closed earlier (within the days): no pop-up, but the tab can still reopen it.
  const skipAuto = !forced && (joined() || knownToKlaviyo() || within(KEYS.closed) || read(KEYS.seen, 'sessionStorage'));

  if (skipAuto) {
    if (!joined() && !knownToKlaviyo() && within(KEYS.closed) && !within(KEYS.tab)) {
      // The tab alone, once the page has settled.
      window.setTimeout(async () => {
        await stylesheet();
        if (!dialog) wire();
        showTab();
      }, 2500);
    }
    return;
  }

  const delay = forced ? 0 : Math.max(5, Number(config.delay) || 5) * 1000;
  const loadedAt = Date.now();
  const phone = window.matchMedia('(max-width: 749px)').matches;
  let scrolled = forced || !phone;

  // Not over anything the visitor is using.
  const busy = () => {
    const banner = document.getElementById('shopify-pc__banner');
    if (banner && banner.getBoundingClientRect().height > 0 && getComputedStyle(banner).visibility !== 'hidden' && getComputedStyle(banner).display !== 'none') return true;
    if (['bag-lock', 'sov-open', 'menu-lock', 'luxe-search-filters-open', 'pdp-cond-open', 'pdp-pass-open', 'ldj-saved-open'].some((name) => root.classList.contains(name))) return true;
    if (document.querySelector('dialog[open]:not(.ldj-popup), .pswp--open')) return true;
    return Boolean(document.activeElement?.matches?.('input, textarea, select'));
  };

  // Phones: a third of the page first, as Klaviyo's form.
  const onScroll = () => {
    const depth = (window.scrollY + window.innerHeight) / Math.max(document.documentElement.scrollHeight, 1);
    if (depth >= 0.33) {
      scrolled = true;
      window.removeEventListener('scroll', onScroll);
    }
  };
  if (!scrolled) window.addEventListener('scroll', onScroll, { passive: true });

  const timer = window.setInterval(() => {
    if (Date.now() - loadedAt < delay || !scrolled || document.hidden || busy()) return;
    window.clearInterval(timer);
    open();
  }, 1000);
})();
